import { useState } from 'react';
import { ChevronDown, ChevronRight, Clock, History } from 'lucide-react';

import { Dialog } from '../../../components/ui/Dialog';
import { useGetImportRuleHistoryQuery } from '../api/parserImportApi';

const HISTORY_LIMIT = 20;

const BATCH_STATUS_STYLES = {
  pending: 'bg-lightGrayColor text-darkGrayColor',
  running: 'bg-purpleColor/15 text-purpleColor',
  completed: 'bg-greenColor/15 text-greenColor',
  completed_with_errors: 'bg-orangeColor/15 text-orangeColor',
  failed: 'bg-redColor/15 text-redColor',
  cancelled: 'bg-lightGrayColor text-darkGrayColor',
};

const ITEM_STATUS_STYLES = {
  created: 'bg-greenColor/15 text-greenColor',
  enriched: 'bg-purpleColor/15 text-purpleColor',
  overwritten: 'bg-orangeColor/15 text-orangeColor',
  skipped: 'bg-lightGrayColor text-darkGrayColor',
  failed: 'bg-redColor/15 text-redColor',
};

const COUNT_PILLS = [
  { key: 'created_count', label: '✨ Created', className: 'bg-greenColor/15 text-greenColor' },
  { key: 'enriched_count', label: '✅ Enriched', className: 'bg-purpleColor/15 text-purpleColor' },
  { key: 'overwritten_count', label: '🔄 Overwritten', className: 'bg-orangeColor/15 text-orangeColor' },
  { key: 'skipped_count', label: '⏭ Skipped', className: 'bg-lightGrayColor text-darkGrayColor' },
  { key: 'failed_count', label: '❌ Failed', className: 'bg-redColor/15 text-redColor' },
];

const formatTimestamp = value => {
  if (!value) return '—';
  try {
    return new Date(value).toLocaleString('en-GB', {
      year: 'numeric',
      month: 'short',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return value;
  }
};

const formatDuration = seconds => {
  if (seconds == null) return '—';
  if (seconds < 60) return `${Math.round(seconds)}s`;
  if (seconds < 3600) return `${Math.floor(seconds / 60)}m ${Math.round(seconds % 60)}s`;
  return `${Math.floor(seconds / 3600)}h ${Math.round((seconds % 3600) / 60)}m`;
};

const StatusPill = ({ status, item = false }) => {
  const styles = item ? ITEM_STATUS_STYLES : BATCH_STATUS_STYLES;
  return (
    <span className={`inline-flex items-center rounded px-2 py-0.5 text-[10px] font-medium ${styles[status] || styles.pending || ITEM_STATUS_STYLES.skipped}`}>
      {status || 'pending'}
    </span>
  );
};

const CountPill = ({ label, value, className }) => (
  <span className={`inline-flex items-center gap-1 rounded px-2 py-1 text-[11px] ${className}`}>
    {label} <strong>{Number(value || 0).toLocaleString('fr-FR')}</strong>
  </span>
);

const truncateError = error => {
  if (!error) return '';
  return error.length > 200 ? `${error.slice(0, 200)}…` : error;
};

export const RuleHistoryModal = ({ rule, token, onClose }) => {
  const [expandedBatchIds, setExpandedBatchIds] = useState(() => new Set());
  const { data, isLoading, isFetching, error } = useGetImportRuleHistoryQuery(
    { token, ruleId: rule?.id, limit: HISTORY_LIMIT },
    { skip: !token || !rule?.id }
  );

  const batches = data?.data ?? [];

  const toggleBatch = batchId => {
    setExpandedBatchIds(current => {
      const next = new Set(current);
      if (next.has(batchId)) next.delete(batchId);
      else next.add(batchId);
      return next;
    });
  };

  const content = (
    <div
      data-testid="rule-history-modal"
      className="flex flex-col w-[94vw] max-w-6xl max-h-[82vh] text-left"
    >
      <div className="flex flex-wrap items-start justify-between gap-3 pr-10 pb-4 border-b border-gray-200 flex-shrink-0">
        <div className="min-w-0">
          <div className="flex items-center gap-2 text-xs text-darkGrayColor uppercase tracking-wider">
            <History size={14} className="text-purpleColor" /> Auto-import rule
          </div>
          <h2 id="modal-title" className="mt-1 text-lg font-semibold text-blackColor truncate">
            Run history: {rule?.name}
          </h2>
          <p className="mt-1 text-xs text-darkGrayColor">
            Last {data?.meta?.returned ?? HISTORY_LIMIT} runs, newest first
          </p>
        </div>
        {isFetching && !isLoading && (
          <span className="text-xs text-darkGrayColor">refreshing…</span>
        )}
      </div>

      <div className="flex-1 min-h-0 overflow-y-auto pt-4 pr-1">
        {isLoading && (
          <div className="flex flex-col gap-2">
            {Array.from({ length: 4 }).map((_, index) => (
              <div key={index} className="h-24 rounded-lg border border-gray-200 bg-lightGrayColor animate-pulse" />
            ))}
          </div>
        )}

        {!isLoading && error && (
          <div className="flex items-center justify-center h-80 text-sm text-redColor">
            Failed to load rule run history.
          </div>
        )}

        {!isLoading && !error && batches.length === 0 && (
          <div className="flex items-center justify-center h-80 text-sm text-darkGrayColor">
            This rule has never run yet. Click &apos;Run now&apos; to trigger it.
          </div>
        )}

        {!isLoading && !error && batches.length > 0 && (
          <div className="flex flex-col gap-3">
            {batches.map(batch => {
              const isExpanded = expandedBatchIds.has(batch.batch_id);
              return (
                <div
                  key={batch.batch_id}
                  data-testid={`rule-history-run-${batch.batch_id}`}
                  className="rounded-lg border border-gray-200 bg-white overflow-hidden"
                >
                  <button
                    type="button"
                    onClick={() => toggleBatch(batch.batch_id)}
                    className="flex w-full items-start gap-3 p-3 text-left hover:bg-lightestGrayColor transition-colors"
                  >
                    <span className="mt-1 text-darkGrayColor">
                      {isExpanded ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
                    </span>
                    <div className="flex-1 min-w-0">
                      <div className="flex flex-wrap items-center gap-2">
                        <span className="text-sm font-semibold text-blackColor">Batch #{batch.batch_id}</span>
                        <StatusPill status={batch.status} />
                        {batch.initiator === 'auto' && (
                          <span className="rounded bg-purpleColor/10 px-1.5 py-0.5 text-[10px] font-semibold text-purpleColor">
                            AUTO
                          </span>
                        )}
                      </div>
                      <div className="mt-1 flex flex-wrap items-center gap-2 text-xs text-darkGrayColor">
                        <Clock size={12} />
                        <span>Started {formatTimestamp(batch.started_at || batch.created_at)}</span>
                        <span>· Duration {formatDuration(batch.duration_seconds)}</span>
                        <span>· Total {Number(batch.total || 0).toLocaleString('fr-FR')}</span>
                      </div>
                      <div className="mt-2 flex flex-wrap gap-1.5">
                        {COUNT_PILLS.map(pill => (
                          <CountPill
                            key={pill.key}
                            label={pill.label}
                            value={batch[pill.key]}
                            className={pill.className}
                          />
                        ))}
                      </div>
                    </div>
                  </button>

                  {isExpanded && (
                    <div className="border-t border-gray-100 bg-lightestGrayColor/60 p-3">
                      {(batch.items ?? []).length === 0 ? (
                        <div className="py-4 text-center text-xs text-darkGrayColor">
                          No per-record items captured for this batch.
                        </div>
                      ) : (
                        <div className="overflow-auto rounded border border-gray-200 bg-white">
                          <table className="min-w-full text-xs">
                            <thead className="bg-gray-50 text-darkGrayColor">
                              <tr>
                                <th className="px-3 py-2 text-left font-semibold">Title</th>
                                <th className="px-3 py-2 text-left font-semibold">Status</th>
                                <th className="px-3 py-2 text-left font-semibold">Error</th>
                                <th className="px-3 py-2 text-left font-semibold">Duration</th>
                              </tr>
                            </thead>
                            <tbody>
                              {batch.items.map(item => (
                                <tr
                                  key={`${batch.batch_id}-${item.parser_advert_id}`}
                                  data-testid={`rule-history-item-${batch.batch_id}-${item.parser_advert_id}`}
                                  className="border-t border-gray-100"
                                >
                                  <td className="px-3 py-2 text-blackColor min-w-[220px]">
                                    <div className="font-medium line-clamp-1">
                                      {item.title || `advert #${item.parser_advert_id}`}
                                    </div>
                                    <div className="text-[10px] text-darkGrayColor">
                                      #{item.parser_advert_id}{item.realty_id ? ` · realty #${item.realty_id}` : ''}
                                    </div>
                                  </td>
                                  <td className="px-3 py-2 whitespace-nowrap">
                                    <StatusPill status={item.status} item />
                                  </td>
                                  <td className="px-3 py-2 max-w-md text-redColor">
                                    {item.error ? (
                                      <span title={item.error} className="line-clamp-2">
                                        {truncateError(item.error)}
                                      </span>
                                    ) : '—'}
                                  </td>
                                  <td className="px-3 py-2 text-darkGrayColor whitespace-nowrap">
                                    {item.duration_ms != null ? `${item.duration_ms} ms` : '—'}
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );

  return (
    <Dialog
      isOpen={!!rule}
      onClose={onClose}
      classes="w-[96vw] max-w-6xl"
      content={content}
    />
  );
};
